// frontend/src/pages/StudentHome.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import WatchGanttChart from "../components/WatchGanttChart";
import MyProgress from "../components/MyProgress";
import ReactPlayer from "react-player";
import { getMe } from "../services/authService";
import { computeTotalWatchTime } from "../utils/watchUtils";
import { API_URL } from "../config";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const CONTENT_COLORS = {
  "Python基礎": "#76B900",
  "最新AI調査": "#1E88E5",
  "その他":     "#757575",
};

const formatTime = (secs) => {
  if (secs === null || secs === undefined || isNaN(secs)) return "N/A";
  if (secs === 0) return "0s";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  
  let result = "";
  if (h > 0) result += `${h}h `;
  // 分が0でも時間が存在する場合は "0m" を表示する（例: 1h 0m 5s）
  if (m > 0 || (h > 0 && s >= 0) ) result += `${m}m `; // m > 0 または (h > 0 かつ s が存在する)
  result += `${s}s`;
  return result.trim() || "0s"; // 全て0の場合のフォールバック
};

const formatTimestampJST = (iso) => {
    if (!iso) return "N/A";
    try {
      const utcDate = new Date(iso.endsWith("Z") ? iso : iso + "Z");
      if (isNaN(utcDate.getTime())) return "N/A";
      return utcDate.toLocaleString("ja-JP", {
        timeZone: "Asia/Tokyo",
        year:   "numeric",
        month:  "2-digit",
        day:    "2-digit",
        hour:   "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (e) {
      console.error("Error formatting timestamp:", iso, e);
      return "N/A";
    }
  };

const MAX_NOTE_LENGTH = 500; 
const MIN_NOTE_LENGTH = 30;

const StudentHome = () => {
  const [videos, setVideos]         = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [studentId, setStudentId]   = useState(null);
  const [logs, setLogs]             = useState([]);
  const [note, setNote]             = useState("");
  const [message, setMessage]       = useState("");
  const [noteError, setNoteError]   = useState("");

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null); 
  const lastEventTimeRef = useRef(Date.now());
  const [currentPlayedSeconds, setCurrentPlayedSeconds] = useState(0);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      setMessage("ログインしてください。");
      return;
    }
    getMe()
      .then((u) => setStudentId(u.id))
      .catch((e) => setMessage(e.message || "ユーザー情報の取得に失敗しました。"));

    axios
      .get(`${API_URL}/videos/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (Array.isArray(res.data)) {
          const formattedVideos = res.data.map(v => ({
            ...v,
            duration: (v.duration === null || v.duration === undefined) ? null : (parseFloat(v.duration) || null)
          }));
          // console.log("StudentHome: Fetched and formatted videos:", formattedVideos); // デバッグ用
          setVideos(formattedVideos);
        } else {
          setVideos([]);
          console.error("StudentHome: 動画一覧の取得データ形式が不正です:", res.data);
        }
      })
      .catch((e) => {
        console.error("StudentHome: 動画一覧の取得エラー:", e);
        setMessage(e.response?.data?.detail || e.message || "動画一覧の取得に失敗しました。");
      });
  }, [token]);

  useEffect(() => {
    if (!selectedVideo || !token) {
      setLogs([]);
      return;
    }
    axios
      .get(`${API_URL}/watch-logs/${selectedVideo.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setLogs(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error(`StudentHome: 視聴ログの取得エラー (videoId: ${selectedVideo.id}):`, err);
        setLogs([]);
      });
  }, [selectedVideo, token]);

  const sendWatchEvent = async (data) => {
    if (studentId == null || !selectedVideo || !token) return;
    const now = Date.now();
    const diffSec = Math.floor((now - lastEventTimeRef.current) / 1000);
    lastEventTimeRef.current = now;

    const payload = {
      student_id: studentId,
      video_id: selectedVideo.id,
      event_type: data.event_type,
      played_seconds:
        data.played_seconds !== undefined
          ? Math.floor(data.played_seconds)
          : Math.floor(currentPlayedSeconds),
      duration_since_last_event: diffSec,
      note_text: data.note_text ?? null,
    };

    try {
      await axios.post(`${API_URL}/watch-logs/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const r = await axios.get(
        `${API_URL}/watch-logs/${selectedVideo.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLogs(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
        console.error("StudentHome: 視聴ログ送信または再取得エラー:", err);
    }
  };

  const handleSelectVideo = (video) => {
    setSelectedVideo(video);
    setCurrentPlayedSeconds(0);
    setLogs([]);
    setNote("");
    setNoteError("");
    sendWatchEvent({ event_type: "start", played_seconds: 0 });
  };
  const handlePlay   = () => sendWatchEvent({ event_type: "resume", played_seconds: currentPlayedSeconds });
  const handlePause  = () => sendWatchEvent({ event_type: "pause",  played_seconds: currentPlayedSeconds });
  const handleEnded  = () => sendWatchEvent({ event_type: "end",    played_seconds: selectedVideo?.duration || currentPlayedSeconds });
  const handleProgress = ({ playedSeconds }) => setCurrentPlayedSeconds(Math.floor(playedSeconds));
  const handleSeek   = (sec) => {
    setCurrentPlayedSeconds(Math.floor(sec));
    sendWatchEvent({ event_type: "seek", played_seconds: Math.floor(sec) });
  }

  const handleNoteChange = (e) => {
    const newNote = e.target.value;
    if (newNote.length <= MAX_NOTE_LENGTH) {
      setNote(newNote);
      if (newNote.trim().length >= MIN_NOTE_LENGTH && noteError) {
         setNoteError("");
      } else if (newNote.trim().length < MIN_NOTE_LENGTH && newNote.length > 0) {
         setNoteError(`ノートは${MIN_NOTE_LENGTH}文字以上で入力してください。`);
      } else if (newNote.length === 0 && noteError && noteError.includes(MIN_NOTE_LENGTH.toString())) {
        setNoteError("");
      }
    } else {
      setNote(newNote.substring(0, MAX_NOTE_LENGTH));
      setNoteError(`ノートは${MAX_NOTE_LENGTH}文字以内で入力してください。`);
    }
  };

  const handleNoteSubmit = () => {
    if (!selectedVideo) return;
    const trimmedNote = note.trim(); 

    if (trimmedNote.length < MIN_NOTE_LENGTH) {
      setNoteError(`ノートは実質${MIN_NOTE_LENGTH}文字以上で入力してください。`);
      return; 
    }
    if (trimmedNote.length > MAX_NOTE_LENGTH) { 
        setNoteError(`ノートが長すぎます（最大${MAX_NOTE_LENGTH}文字）。`);
        return;
    }

    sendWatchEvent({ event_type: "note", played_seconds: currentPlayedSeconds, note_text: trimmedNote });
    setNote(""); 
    setNoteError(""); 
  };
  
  const totalTime  = useMemo(() => {
      if (!logs || logs.length === 0) return 0;
      return computeTotalWatchTime(logs);
  }, [logs]);

  const notesCount = logs ? logs.filter((e) => e.event_type === "note" && e.note_text).length : 0;

  const pastNotes = logs
    ? logs
        .filter((e) => e.event_type === "note" && e.note_text)
        .map((e) => ({
          id: e.id,
          pos: e.played_seconds,
          text: e.note_text,
          ts: e.timestamp,
        }))
        .sort((a,b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
    : [];

  useEffect(() => {
    if (selectedVideo && playerContainerRef.current) {
      playerContainerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedVideo]);
 
  // 動画の再生時間が有効かどうかをチェック
  const currentVideoDuration = selectedVideo ? parseFloat(selectedVideo.duration) : null;
  const videoDurationIsValid = currentVideoDuration !== null && !isNaN(currentVideoDuration) && currentVideoDuration > 0;
  //console.log("StudentHome: currentVideoDuration:", currentVideoDuration, "videoDurationIsValid:", videoDurationIsValid);
  const showGantt = logs && logs.length > 0 && videoDurationIsValid;

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4}}>
      <Typography variant="h5" gutterBottom>学習ページ</Typography>
      {message && <Typography color="error" sx={{mb:2}}>{message}</Typography>}

      <MyProgress />


      <Typography variant="h6" gutterBottom>動画一覧</Typography>
      <Box sx={{ position: "relative", border: "1px solid #ccc", borderRadius: 1, maxHeight: 300, overflowY: "auto", p: 1, mb: 4, backgroundColor: "rgba(255,255,255,0.05)" }}>
          <List disablePadding>
            {videos.map((v) => (
              <ListItem key={v.id} disablePadding sx={{borderBottom: '1px solid rgba(255,255,255,0.08)'}}>
                <ListItemButton
                  selected={selectedVideo?.id === v.id}
                  onClick={() => handleSelectVideo(v)}
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, py: 1.5, "&:hover": { backgroundColor: "action.hover" }, ...(selectedVideo?.id === v.id && { backgroundColor: 'primary.dark', '&:hover': { backgroundColor: 'primary.main'} }) }}
                >
                  <ListItemText primary={<strong>{v.title}</strong>} primaryTypographyProps={{ color: selectedVideo?.id === v.id ? 'common.white' : 'text.primary',  fontWeight: selectedVideo?.id === v.id ? 'bold' : 'normal' }} />
                  <Chip label={v.content_type || "未分類"} size="small" sx={{ textTransform: "none", backgroundColor: CONTENT_COLORS[v.content_type] || CONTENT_COLORS["その他"], color: "#fff", ml:1 }}/>
                </ListItemButton>
              </ListItem>
            ))}
            {videos.length === 0 && !message && (<Typography sx={{p:2, textAlign:'center', color:'text.secondary'}}>視聴可能な動画がありません。</Typography>)}
          </List>
          {videos.length > 5 && (<Box sx={{ position: "sticky", bottom: 0, width: "100%", height: 40, pointerEvents: "none", background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)", display: "flex", alignItems: "flex-end", justifyContent: "center" }}><KeyboardArrowDownIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 30 }} /></Box>)}
        </Box>

      {selectedVideo && (
        <Box id="player-section" ref={playerContainerRef} sx={{pt:2}}> 
          <Typography variant="h6" gutterBottom>{selectedVideo.title}</Typography>
          <Box sx={{position: 'relative', paddingTop: '56.25%', backgroundColor: '#000'}}>
            <ReactPlayer
                ref={playerRef}
                url={selectedVideo.url}
                controls
                playing
                width="100%"
                height="100%"
                style={{ position: 'absolute', top: 0, left: 0 }}
                onPlay={handlePlay}
                onPause={handlePause}
                onEnded={handleEnded}
                onProgress={handleProgress}
                onSeek={handleSeek}
            />
          </Box>
          
          {showGantt && ( 
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>視聴パターン</Typography>
              <WatchGanttChart logs={logs} videoDuration={currentVideoDuration} /> 
            </Box>
          )}
          
          <Box sx={{mt: (showGantt ? 1 : 3), mb:3, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Typography variant="body2">合計視聴時間: <strong>{formatTime(totalTime)}</strong></Typography>
            <Typography variant="body2">ノート件数: <strong>{notesCount}</strong></Typography>
          </Box>

          <Box sx={{ my: 2 }}>
            <TextField
              label={`動画「${selectedVideo.title}」の学びのメモ`}
              placeholder={`視聴位置 @${formatTime(currentPlayedSeconds)} 付近のメモ（${MIN_NOTE_LENGTH}文字以上、${MAX_NOTE_LENGTH}文字以内）`}
              multiline
              fullWidth
              minRows={3}
              value={note}
              onChange={handleNoteChange}
              error={!!noteError}
              helperText={noteError || `${note.length}/${MAX_NOTE_LENGTH} 文字`}
              variant="filled"
              sx={{mb:1}}
            />
            <Button variant="contained" onClick={handleNoteSubmit} disabled={note.trim().length < MIN_NOTE_LENGTH || !!noteError || note.length > MAX_NOTE_LENGTH}>
              この位置でノートを記録
            </Button>
          </Box>

          {pastNotes.length > 0 && (
            <Box sx={{ mt:4, mb: 3 }}>
              <Typography variant="h6" gutterBottom>この動画の過去のノート</Typography>
              <List>
                {pastNotes.map((n) => (
                  <ListItem key={n.id} divider sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                    <ListItemText
                      primary={ <Typography variant="body2" component="span"> <strong>@{formatTime(n.pos ?? 0)}:</strong> {n.text} </Typography> }
                      secondary={formatTimestampJST(n.ts)}
                      secondaryTypographyProps={{variant: 'caption', display:'block', mt:0.5}}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default StudentHome;